import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Calendar, ChevronLeft, ChevronRight, Clock, Users } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import 'leaflet/dist/leaflet.css';
import { API_BASE as API } from '../lib/apiBase';


const createIcon = (color = '#00D4FF') => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:20px;height:20px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 10px ${color}80;"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -14]
});

const defaultIcon = createIcon('#00D4FF');
const todayIcon = createIcon('#4caf50');

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getMonthName(month, lang) {
  const names_es = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const names_en = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return lang === 'es' ? names_es[month] : names_en[month];
}

function getDayName(day, lang) {
  const es = ['L','M','X','J','V','S','D'];
  const en = ['M','T','W','T','F','S','S'];
  return lang === 'es' ? es[day] : en[day];
}

export default function EventsPage() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapSettings, setMapSettings] = useState({
    default_map_lat: 20,
    default_map_lng: 0,
    default_map_zoom: 2,
    events_map_caption_es: '',
    events_map_caption_en: '',
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetch(`${API}/api/admin/settings/public`)
      .then(r => r.json())
      .then((d) => {
        setMapSettings((prev) => ({
          ...prev,
          default_map_lat: typeof d.default_map_lat === 'number' ? d.default_map_lat : 20,
          default_map_lng: typeof d.default_map_lng === 'number' ? d.default_map_lng : 0,
          default_map_zoom: typeof d.default_map_zoom === 'number' ? d.default_map_zoom : 2,
          events_map_caption_es: d.events_map_caption_es || '',
          events_map_caption_en: d.events_map_caption_en || '',
        }));
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetch(`${API}/api/events?limit=100`)
      .then(r => r.json())
      .then(data => { setEvents(data.events || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const selectedStr = selectedDate.toISOString().split('T')[0];

  // Filter events for selected date
  const filteredEvents = events.filter(e => {
    if (!e.date) return false;
    const evDate = e.date.split('T')[0];
    return evDate === selectedStr;
  });

  // Events on map (all events or filtered by date)
  const mapEvents = filteredEvents.length > 0 ? filteredEvents : events;

  // Calendar data
  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const firstDayMon = firstDay === 0 ? 6 : firstDay - 1;

  // Days with events
  const eventDates = new Set(events.map(e => e.date?.split('T')[0]).filter(Boolean));

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const selectDay = (day) => {
    setSelectedDate(new Date(calYear, calMonth, day));
  };

  const mapCenter = [mapSettings.default_map_lat, mapSettings.default_map_lng];
  const mapZoom = mapSettings.default_map_zoom;
  const mapCaption = lang === 'es' ? (mapSettings.events_map_caption_es || 'Mapa de eventos con OpenStreetMap (global).') : (mapSettings.events_map_caption_en || 'Event map with OpenStreetMap (global).');

  return (
    <div data-testid="events-page" className="min-h-screen pt-14 pb-20">
      <div className="px-4 md:px-12 py-4">
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
          <Calendar size={22} className="text-pulse-primary" />
          {lang === 'es' ? 'Eventos y Conciertos' : 'Events & Concerts'}
        </h1>
        <p className="text-xs text-pulse-muted mt-1">{mapCaption}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 md:px-12">
        {/* Calendar */}
        <div className="lg:col-span-1">
          <div className="bg-pulse-surface border border-pulse-border rounded-2xl p-4">
            {/* Month nav */}
            <div className="flex items-center justify-between mb-4">
              <button data-testid="calendar-prev-month" onClick={prevMonth} className="p-2 hover:bg-pulse-elevated rounded-lg transition-colors"><ChevronLeft size={16} /></button>
              <h3 className="font-heading font-semibold text-sm">{getMonthName(calMonth, lang)} {calYear}</h3>
              <button data-testid="calendar-next-month" onClick={nextMonth} className="p-2 hover:bg-pulse-elevated rounded-lg transition-colors"><ChevronRight size={16} /></button>
            </div>

            {/* Day names */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {[0,1,2,3,4,5,6].map(d => (
                <div key={d} className="text-center text-[10px] text-pulse-muted font-medium py-1">{getDayName(d, lang)}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {Array(firstDayMon).fill(null).map((_, i) => <div key={`empty-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedStr;
                const hasEvent = eventDates.has(dateStr);

                return (
                  <button
                    key={day}
                    data-testid={`cal-day-${day}`}
                    onClick={() => selectDay(day)}
                    className={`relative w-full aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-all duration-200
                      ${isSelected ? 'bg-pulse-primary text-black' : isToday ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-pulse-elevated'}
                      ${hasEvent && !isSelected ? 'text-white' : ''}
                    `}
                  >
                    {day}
                    {hasEvent && (
                      <span className={`absolute bottom-0.5 w-1 h-1 rounded-full ${isSelected ? 'bg-black' : 'bg-pulse-primary'}`} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected date info */}
            <div className="mt-4 pt-3 border-t border-pulse-border">
              <p className="text-xs text-pulse-muted">
                {selectedDate.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <p className="text-sm font-semibold mt-1">
                {filteredEvents.length > 0
                  ? `${filteredEvents.length} ${lang === 'es' ? 'eventos' : 'events'}`
                  : (lang === 'es' ? 'Sin eventos este dia' : 'No events this day')}
              </p>
            </div>
          </div>

          {/* Event list for selected date */}
          <div className="mt-4 space-y-2">
            {filteredEvents.map(ev => {
              const title = lang === 'en' ? (ev.title_en || ev.title_es) : ev.title_es;
              return (
                <div key={ev.id} data-testid={`event-card-${ev.id}`} className="bg-pulse-surface border border-pulse-border rounded-xl p-4 hover:border-cyan-500/20 transition-colors">
                  <div className="flex items-start gap-3">
                    {ev.image && <img src={ev.image} alt={title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-semibold text-sm">{title}</h4>
                      {ev.artist_names?.length > 0 && (
                        <p className="text-xs text-pulse-primary mt-0.5">{ev.artist_names.join(', ')}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-pulse-muted">
                        <span className="flex items-center gap-1"><Clock size={10} /> {ev.time || '21:00'}</span>
                        <span className="flex items-center gap-1"><MapPin size={10} /> {ev.location?.venue || ev.city || ''}</span>
                        {ev.capacity && <span className="flex items-center gap-1"><Users size={10} /> {ev.capacity}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredEvents.length === 0 && !loading && (
              <div className="text-center py-6">
                <Calendar size={24} className="mx-auto text-pulse-muted opacity-30 mb-2" />
                <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Selecciona un dia con punto para ver eventos' : 'Select a day with a dot to see events'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div data-testid="events-map-wrapper" className="lg:col-span-2 h-[60vh] lg:h-auto min-h-[400px] rounded-2xl overflow-hidden border border-pulse-border">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center bg-pulse-surface">
              <div className="w-8 h-8 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <MapContainer center={mapCenter} zoom={mapZoom} style={{ width: '100%', height: '100%' }} scrollWheelZoom={true}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
              />
              {mapEvents.filter(e => e.location?.lat && e.location?.lng).map(ev => {
                const title = lang === 'en' ? (ev.title_en || ev.title_es) : ev.title_es;
                const evDateStr = ev.date?.split('T')[0];
                const isFiltered = evDateStr === selectedStr;
                return (
                  <Marker key={ev.id} position={[ev.location.lat, ev.location.lng]} icon={isFiltered ? todayIcon : defaultIcon}>
                    <Popup className="pulse-popup">
                      <div className="text-xs" style={{color: '#000', minWidth: 180}}>
                        <p className="font-bold text-sm">{title}</p>
                        {ev.artist_names?.length > 0 && <p className="text-blue-600 mt-0.5">{ev.artist_names.join(', ')}</p>}
                        <p className="mt-1 text-gray-600">{ev.location.venue}</p>
                        <p className="text-gray-500">{new Date(ev.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })} {ev.time || '21:00'}</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
}
